import { DeleteOutlined, EditOutlined, UserOutlined } from '@ant-design/icons';
import { UserAvatar } from '@app/components/common/UserAvatar/UserAvatar';
import { IChatPeople, IMessage } from '@app/interfaces/message';
import {
  canEditOrDeleteMessage,
  firstLatterOfName,
  getRawMessage,
  getReadableFileName,
  getTabId,
} from '@app/utils/utils';
import { Avatar, Popconfirm, Tooltip } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import * as S from './ChatView.styles';
import { openFile } from '@app/utils/file';
import { IFile } from '@app/interfaces/file';
import { useTranslation } from 'react-i18next';
import { Badge } from '@app/components/common/Badge/Badge';
import { IUser } from '@app/interfaces/user';
import { EditChatMessage } from './EditChatMessage/EditChatMessage';
import { notificationController } from '@app/controllers/notificationController';
import { IDealRoomShare } from '@app/interfaces/deal-room';
import {
  deleteDealRoomComment,
  deleteDealRoomCommentExternal,
  editDealRoomComment,
  editDealRoomCommentExternal,
} from '@app/api/deal-room.api';
import { useMediaQuery } from 'react-responsive';
import { Spinner } from '@app/components/common/Spinner/Spinner';
import { DealRoomStatus } from '@app/constants/enums/deal-room';
import { getDealRoomStatus } from '@app/utils/options';
import { downloadDealRoomInfoFile } from '@app/api/deal-room-info.api';

interface ChatViewProps {
  isDealRoomClosed: boolean;
  status: DealRoomStatus;
  messages: IMessage[];
  isDrawer: boolean;
  user: IUser | null;
  dealRoom: IDealRoomShare;
  users: IChatPeople[];
  showUsers?: boolean;
  isTablet?: boolean;
  people?: IChatPeople | null;
  onChangePeople?: (user: IChatPeople) => void;
  onWhatsAppClick?: () => void;
  onSmsClick?: () => void;
  onEmailClick?: () => void;
  onChatRefresh?: (isFile: boolean) => void;
}

export const ChatView: React.FC<ChatViewProps> = ({
  isDealRoomClosed,
  status,
  messages,
  isDrawer,
  user,
  dealRoom,
  users,
  isTablet,
  showUsers = false,
  people,
  onChangePeople,
  onWhatsAppClick,
  onSmsClick,
  onEmailClick,
  onChatRefresh,
}) => {
  const { t } = useTranslation();
  const messagesRef = useRef(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const [hovered, setHovered] = useState<string | null>();
  const [isEditChatOpen, setIsEditChatOpen] = useState(false);
  const [selectedChat, setSelectedChat] = useState<IMessage | null>(null);
  const [loading, setLoading] = useState(false);
  const [key, setKey] = useState(0);
  const [emailDisabled, setEmailDisabled] = useState(false);

  const downloadFile = async (file: IFile) => {
    try {
      const data = await downloadDealRoomInfoFile(dealRoom?.id as string, file.id as string, {
        sessionId: dealRoom?.sessionId,
        uniqueId: dealRoom?.uniqueId,
        userId: user?.id,
      });
      openFile(data.id);
    } catch (error) {
      notificationController.error({
        message: t('dealRooms.form.errorDownloadingFile'),
      });
    }
  };

  const getToolTip = (user: IChatPeople) => {
    const name = `${user.name}${user.phone ? ` (${user.phone})` : ''}`;
    if (onChangePeople) {
      return t('dealRooms.landing.clickToChatWith', {
        name,
      });
    }
    return name;
  };

  const onEditMessage = (message: IMessage) => {
    setSelectedChat(message);
    setIsEditChatOpen(true);
  };

  const getEmailToolTip = () => {
    return emailDisabled
      ? t('dealRooms.landing.emailDisabledNote', {
          status: getDealRoomStatus(t, status),
        })
      : null;
  };

  const onEditSuccess = async (values: { message: string }) => {
    if (selectedChat) {
      const canEdit = canEditOrDeleteMessage(selectedChat, user);
      if (!canEdit) {
        notificationController.error({
          message: t('chats.editOrDeleteNote', {
            minutes: user
              ? Number(process.env.REACT_APP_ADMIN_CHAT_EDITABLE_TIME || 0)
              : Number(process.env.REACT_APP_CLIENT_CHAT_EDITABLE_TIME || 0),
          }),
        });
        setKey((prev) => prev + 1);
        return;
      }
      setLoading(true);
      try {
        const payload = {
          message: values.message,
          tabId: getTabId() || null,
        };
        user
          ? await editDealRoomComment(dealRoom?.id as string, selectedChat.id, payload)
          : await editDealRoomCommentExternal(dealRoom?.id as string, selectedChat.id, {
              ...payload,
              uniqueId: dealRoom?.uniqueId as string,
              sessionId: dealRoom?.sessionId as string,
            });
        notificationController.success({ message: t('chats.editSuccessfully') });
        if (onChatRefresh) {
          onChatRefresh(!!selectedChat.file || !!selectedChat.link);
        }
        setSelectedChat(null);
        setIsEditChatOpen(false);
      } catch (error) {
        notificationController.error({ message: (error as Error).message });
      } finally {
        setLoading(false);
      }
    }
  };

  const onDelete = async (message: IMessage) => {
    const canEdit = canEditOrDeleteMessage(message, user);
    if (!canEdit) {
      notificationController.error({
        message: t('chats.editOrDeleteNote', {
          minutes: user
            ? Number(process.env.REACT_APP_ADMIN_CHAT_EDITABLE_TIME || 0)
            : Number(process.env.REACT_APP_CLIENT_CHAT_EDITABLE_TIME || 0),
        }),
      });
      setKey((prev) => prev + 1);
      return;
    }
    setLoading(true);
    try {
      const payload = {
        tabId: getTabId() || null,
      };
      user
        ? await deleteDealRoomComment(dealRoom?.id as string, message.id, payload)
        : await deleteDealRoomCommentExternal(dealRoom?.id as string, message.id, {
            ...payload,
            uniqueId: dealRoom?.uniqueId as string,
            sessionId: dealRoom?.sessionId as string,
          });
      notificationController.success({ message: t('chats.deleteSuccessfully') });
      if (onChatRefresh) {
        onChatRefresh(!!message.file || !!message.link);
      }
    } catch (error) {
      notificationController.error({ message: (error as Error).message });
    } finally {
      setLoading(false);
    }
  };

  const handleTouchStart = (id: string) => {
    timeoutRef.current = setTimeout(function () {
      setHovered(id);
    }, 1000); // long press = 1000ms
  };

  const handleTouchEnd = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
  };

  useEffect(() => {
    if (messagesRef.current) {
      (messagesRef.current as HTMLDivElement).scrollTop = (messagesRef.current as HTMLDivElement).scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    setEmailDisabled([DealRoomStatus.OPEN_FOR_REPRESENTATIVE, DealRoomStatus.CLOSED].includes(status));
  }, [status]);

  return (
    <Spinner spinning={loading}>
      <S.ChatContainer className={`chat-messages ${!isDrawer ? 'chat-layout' : ''}`} key={key}>
        {isEditChatOpen && selectedChat && (
          <EditChatMessage
            open={isEditChatOpen}
            selectedChat={selectedChat}
            loading={loading}
            onCancel={() => setIsEditChatOpen(false)}
            onEditChat={onEditSuccess}
          />
        )}
        {user && (
          <S.ActionWrapper>
            <Tooltip title={getEmailToolTip()}>
              <S.Link
                onClick={() => {
                  if (onSmsClick && !emailDisabled) {
                    onSmsClick();
                  }
                }}
                className={emailDisabled ? 'disabled' : ''}
              >
                {t('dealRooms.landing.sms')}
              </S.Link>
            </Tooltip>
            |
            <Tooltip title={getEmailToolTip()}>
              <S.Link
                onClick={() => {
                  if (onWhatsAppClick && !emailDisabled) {
                    onWhatsAppClick();
                  }
                }}
                className={emailDisabled ? 'disabled' : ''}
              >
                {t('dealRooms.landing.wa')}
              </S.Link>
            </Tooltip>
            |
            <Tooltip title={getEmailToolTip()}>
              <S.Link
                onClick={() => {
                  if (onEmailClick && !emailDisabled) {
                    onEmailClick();
                  }
                }}
                className={emailDisabled ? 'disabled' : ''}
              >
                {t('dealRooms.landing.email')}
              </S.Link>
            </Tooltip>
          </S.ActionWrapper>
        )}
        {showUsers && users.length > 0 && (
          <S.UserList>
            {t('dealRooms.landing.participants')}:
            {users.map((user) => (
              <Tooltip key={user.id} title={isTablet ? getToolTip(user) : null}>
                {user.isUser ? (
                  <UserAvatar size={36} noMargin={true} image={user.image} tooltip={user.name} />
                ) : (
                  <Badge dot={user.unread} offset={[4, 6]} size="default">
                    <Avatar
                      size={36}
                      style={{ backgroundColor: '#FCC3A2' }}
                      className={people && people.id === user.id ? 'active' : ''}
                      onClick={() => onChangePeople && onChangePeople(user)}
                    >
                      {firstLatterOfName(user.name)}
                    </Avatar>
                  </Badge>
                )}
              </Tooltip>
            ))}
          </S.UserList>
        )}
        <S.MessagesWrapper ref={messagesRef}>
          {messages.map((msg) => (
            <S.MessageRow
              key={msg.id}
              isOwn={msg.isOwn}
              onTouchStart={() => handleTouchStart(msg.id)}
              onTouchEnd={() => handleTouchEnd}
              onMouseEnter={() => (!isMobile ? setHovered(msg.id) : null)}
              onMouseLeave={() => (!isMobile ? setHovered(null) : null)}
            >
              {!msg.isOwn &&
                (msg.senderImage ? (
                  <UserAvatar size={36} image={msg.senderImage} tooltip={msg.senderName} />
                ) : (
                  <Tooltip title={msg.senderName} placement="top">
                    <Avatar size={36} style={{ backgroundColor: '#FCC3A2' }}>
                      {firstLatterOfName(msg.senderName)}
                    </Avatar>
                  </Tooltip>
                ))}
              <S.MessageBubble isOwn={msg.isOwn}>
                {msg.file && (
                  <S.Message>
                    <a onClick={() => downloadFile(msg.file as IFile)}>
                      {getReadableFileName(msg.file.originalName as string)}
                    </a>
                  </S.Message>
                )}
                {msg.link && (
                  <S.Message>
                    <a href={msg.link} target="_blank" rel="noreferrer">
                      {getReadableFileName(msg.link)}
                    </a>
                  </S.Message>
                )}
                <S.Message dangerouslySetInnerHTML={getRawMessage(msg.message)} dir={msg.dir} />
                <S.Time>
                  {msg.isSentAdmin && <UserOutlined />}
                  {msg.createdAt}
                </S.Time>
                <S.SenderName
                  className={
                    msg.isOwn && hovered === msg.id && canEditOrDeleteMessage(msg, user) && !isDealRoomClosed
                      ? 'own-message'
                      : msg.isOwn && canEditOrDeleteMessage(msg, user)
                      ? 'personal-message'
                      : ''
                  }
                >
                  {msg.isOwn ? t('common.you') : msg.senderName}
                  {hovered === msg.id && canEditOrDeleteMessage(msg, user) && !isDealRoomClosed && (
                    <S.MessageOptions>
                      <Popconfirm
                        title={t('chats.areYouSureDeleteMessage')}
                        okText={t('common.yes')}
                        cancelText={t('common.no')}
                        onConfirm={(e) => {
                          e?.stopPropagation();
                          onDelete(msg);
                        }}
                        onCancel={(e) => e?.stopPropagation()}
                      >
                        <Tooltip title={t('chats.delete')}>
                          <DeleteOutlined
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                          />
                        </Tooltip>
                      </Popconfirm>
                      <Tooltip title={t('chats.edit')}>
                        <EditOutlined onClick={() => onEditMessage(msg)} />
                      </Tooltip>
                    </S.MessageOptions>
                  )}
                </S.SenderName>
              </S.MessageBubble>
              {msg.isOwn &&
                (msg.senderImage ? (
                  <UserAvatar size={36} image={msg.senderImage} tooltip={msg.senderName} />
                ) : (
                  <Tooltip title={msg.senderName} placement="top">
                    <Avatar size={36} style={{ backgroundColor: '#FCC3A2' }}>
                      {firstLatterOfName(msg.senderName)}
                    </Avatar>
                  </Tooltip>
                ))}
            </S.MessageRow>
          ))}
        </S.MessagesWrapper>
      </S.ChatContainer>
    </Spinner>
  );
};
